import type { Polygon } from "geojson";
import { wkbToPolygon } from "../../../../../utils/wkbUtils";
import { fx_clienteMapaRegistro } from "./MapaRegistroFinca";

/** Lee la geometría de una pieza padre, tal como está en la base.
 *
 *  **Para qué.** Al entrar a editar una pieza, la geometría que se le entrega
 *  al motor de vértices sale de acá y no de una copia del cliente. Antes salía
 *  de la capa de Leaflet que había en memoria, con el argumento de que era "la
 *  misma que se va a guardar"; con las piezas guardadas renderizadas sólo por
 *  WMS, la copia del cliente dejó de ser la referencia: la referencia es la
 *  base, que es lo que el usuario está viendo dibujado.
 *
 *  **Va por gRPC** (`LeerPiezaPadre`) desde el 2026-10-11, con la validación de
 *  que el registro sea del cliente. Antes era un GetFeature de WFS directo a
 *  GeoServer con el usuario administrador de GeoServer en la web. El nombre de
 *  la clase quedó de cuando iba por WFS.
 *
 *  **Es de SOLO LECTURA.** El vértice movido se guarda por el `GuardarAoi` de
 *  siempre, que además es el que recalcula el perímetro unido y valida solapes
 *  y áreas contra el resto del registro.
 *
 *  **El `indice` es la posición de la pieza**, 1-based, el mismo número que
 *  `AoiPieza.indice` (ver `AoiPiezasState`).
 */
/** Tolerancia, en grados, para aceptar que la geometría que devolvió el
 *  servidor es la MISMA pieza que tiene el cliente. ~1,1 m.
 *
 *  Está puesta entre dos escalas que no se pisan: el proyecto redondea a 7
 *  decimales (`PRECISION_COORDENADAS`, ~1 cm), así que dos copias de la misma
 *  pieza no pueden diferir más que eso; y el caso que hay que atajar —GeoServer
 *  publicando con su default de 4 decimales— mete un error de hasta ~5,5 m. */
const TOLERANCIA_GRADOS = 1e-5;

/** ¿Las dos geometrías son la misma pieza, salvo redondeo?
 *
 *  **Para qué.** Para no editar una versión degradada de la pieza. GeoServer
 *  publica las coordenadas con los decimales que diga `numDecimals` de la capa,
 *  y una capa recién publicada queda en `0`, que no significa "todos" sino "usá
 *  el default" — y el default son **4 decimales, ~11 m**. Con eso los handles
 *  salen corridos del borde y la forma se ve deformada: vértices vecinos
 *  colapsan en el mismo punto de una grilla gruesa. Y es peor que un problema
 *  visual: mover un vértice guarda la geometría ENTERA, así que una lectura
 *  degradada se escribiría de vuelta a la base y destrozaría el resto del
 *  polígono.
 *
 *  Desde el 2026-10-11 la pieza no viene de GeoServer sino de la base por gRPC
 *  (`LeerPiezaPadre`), con todos sus decimales, así que ese caso ya no se da. El
 *  chequeo queda como red: si la respuesta no coincide con lo que el cliente ya
 *  tiene, se descarta y se edita la copia del modelo.
 *
 *  Se comparan vértice a vértice y por posición, no por cercanía: las dos son la
 *  misma pieza, con los mismos vértices en el mismo orden. Si no lo son, la
 *  respuesta no sirve y da igual por qué. */
export function fx_esLaMismaPieza(a: Polygon, b: Polygon): boolean {
  if (a.coordinates.length !== b.coordinates.length) return false;
  for (let anillo = 0; anillo < a.coordinates.length; anillo++) {
    const ra = a.coordinates[anillo];
    const rb = b.coordinates[anillo];
    if (ra.length !== rb.length) return false;
    for (let i = 0; i < ra.length; i++) {
      if (Math.abs(ra[i][0] - rb[i][0]) > TOLERANCIA_GRADOS) return false;
      if (Math.abs(ra[i][1] - rb[i][1]) > TOLERANCIA_GRADOS) return false;
    }
  }
  return true;
}

export class PoligonosPadreWfsService {
  constructor(private readonly _registroId: string) {}

  /** La geometría de una pieza, o `null` si no está o no se pudo leer.
   *
   *  Un `null` NO es fatal para el llamador: puede seguir con la geometría que
   *  tiene en el modelo. Es el camino degradado, no un error de pantalla.
   */
  async leerAsync(indice: number): Promise<Polygon | null> {
    if (!this._registroId) return null;

    try {
      const respuesta = await fx_clienteMapaRegistro().leerPiezaPadre({
        registroId: this._registroId,
        indice,
      });
      if (!respuesta.geometria) return null;
      const geometria = wkbToPolygon(respuesta.geometria);
      return geometria.coordinates?.length ? geometria : null;
    } catch (error) {
      // Sin respuesta el llamador sigue con lo que tiene en el modelo.
      console.error("No se pudo leer la pieza del registro", error);
      return null;
    }
  }

  /* **No hay un `solapaConOtraPiezaAsync` ni un `esFormaValidaAsync` acá.**
   *
   *  Los dos le preguntaban a GeoServer por WFS (un GetFeature con filtro
   *  espacial y el polígono entero en GML) y se quitaron el 2026-10-11, cuando
   *  la web dejó de hablar con GeoServer. El primero ya no lo llamaba nadie: la
   *  validación de solape del modo Editar la hace el servidor dentro de la misma
   *  transacción que aplica el vértice (`EditarVerticePoligonoPadre`). El
   *  segundo no funcionaba (un filtro OGC no expresa "esta geometría es
   *  válida"); ese chequeo vive en el servidor: `GuardarAoi` valida `IsValid`
   *  sobre el área y sobre cada pieza y rechaza con `GEOMETRIA_INVALIDA`, y el
   *  cliente deshace la operación cuando ese rechazo llega (ver
   *  `AoiDrawToolbar.revertirEdicionRechazada`). */
}
